import {
  BOXES,
  STAIRS,
  EDGE,
  LEAK_EDGE,
  T_BLANK,
  T_SKETCH,
  DRAWN_H,
  DECALS,
  HISS,
  KEEL,
  TOOTH,
  MARKS,
  ALL_NODES,
  STUFF,
  MAP_H,
  MAP_W,
  OLD_H,
  OLD_W,
  TOWNS,
  T_ASH,
  T_COUNTER,
  T_COPPER,
  T_CRUST,
  T_DINER,
  T_GRASS,
  T_DIRT,
  T_FLOOR,
  T_GLASS,
  T_GLOW,
  T_GREASE,
  T_MARROW,
  T_MUD,
  T_PROP,
  T_RIB,
  T_ROAD,
  T_ROCK,
  T_SALT,
  T_SCRUB,
  T_SHARD,
  T_STATION,
  T_STATIC,
  T_TOXIC,
  T_TRACK,
  T_TREE,
  T_VENT,
  T_WALL,
  T_WATER,
  TILE,
  isWall,
  tiles,
} from '../shared/content.ts';
import { GEN, type Cache } from '../shared/worldgen.ts';
import { RARITY_COLOR, type Rarity } from '../shared/items.ts';
import type { DropSnap, EnemyKind, Look, OriginId, Pose } from '../shared/types.ts';
import { boomLights, drawBooms, noteBossBoom, drawChar, drawGroundTile, drawLiquid, drawObject, drawShotArt, hasChar, noteBoom, shotRgb } from './art.ts';
import { drawDarkness, drawGlow, drawParticles, stepFx, type EmitSpec, type GlowSpec } from './fx.ts';
import type { FxSnap, ShotStyle } from '../shared/types.ts';
import {
  gaitGround,
  drawClerk,
  drawCrab,
  drawDog,
  drawCount,
  drawDrawer,
  drawDrifter,
  drawForeman,
  drawFilament,
  drawHound,
  drawKettle,
  drawMast,
  drawMaster,
  drawLeaker,
  drawMite,
  drawMoth,
  drawNail,
  drawNewt,
  drawPanback,
  drawPerson,
  spriteFor,
  drawRoach,
  drawSkip,
  drawSleeper,
  drawSlick,
  drawSlug,
  drawTick,
  drawToad,
  drawWalker,
  drawWanderer,
  drawBlot,
  drawSurveyor,
  drawMainspring,
  drawLastNumber,
} from './sprites.ts';

export type DrawActor = {
  id: number;
  kind: 'player' | EnemyKind | 'dog' | 'npc';
  x: number;
  y: number;
  aim: number;
  hp: number;
  maxHp: number;
  name: string;
  pose: Pose;
  look?: Look;
  weapon?: string | null;
  origin?: OriginId;
  dead?: boolean;
  flash?: boolean;
  stun?: boolean;
  npc?: 'brine' | 'peck';
  champ?: boolean;
  boss?: boolean;
};

const FONT: Record<string, string[]> = {
  A: ['.#.', '#.#', '###', '#.#', '#.#'],
  B: ['##.', '#.#', '##.', '#.#', '##.'],
  C: ['###', '#..', '#..', '#..', '###'],
  D: ['##.', '#.#', '#.#', '#.#', '##.'],
  E: ['###', '#..', '##.', '#..', '###'],
  F: ['###', '#..', '##.', '#..', '#..'],
  G: ['##.', '#..', '#.#', '#.#', '.##'],
  H: ['#.#', '#.#', '###', '#.#', '#.#'],
  I: ['.#.', '.#.', '.#.', '.#.', '.#.'],
  J: ['..#', '..#', '..#', '#.#', '.#.'],
  K: ['#.#', '#.#', '##.', '#.#', '#.#'],
  L: ['#..', '#..', '#..', '#..', '###'],
  M: ['#.#', '###', '#.#', '#.#', '#.#'],
  N: ['#.#', '##.', '#.#', '#.#', '#.#'],
  O: ['.#.', '#.#', '#.#', '#.#', '.#.'],
  P: ['##.', '#.#', '##.', '#..', '#..'],
  Q: ['.#.', '#.#', '#.#', '.##', '##.'],
  R: ['##.', '#.#', '##.', '#.#', '#.#'],
  S: ['.##', '#..', '.#.', '..#', '##.'],
  T: ['###', '.#.', '.#.', '.#.', '.#.'],
  U: ['#.#', '#.#', '#.#', '#.#', '.##'],
  V: ['#.#', '#.#', '#.#', '#.#', '.#.'],
  W: ['#.#', '#.#', '#.#', '###', '#.#'],
  X: ['#.#', '#.#', '.#.', '#.#', '#.#'],
  Y: ['#.#', '#.#', '.#.', '.#.', '.#.'],
  Z: ['###', '..#', '.#.', '#..', '###'],
  '.': ['...', '...', '...', '...', '.#.'],
  "'": ['.#.', '.#.', '...', '...', '...'],
  '!': ['.#.', '.#.', '.#.', '...', '.#.'],
  '+': ['...', '.#.', '###', '.#.', '...'],
  '-': ['...', '...', '###', '...', '...'],
  ',': ['...', '...', '...', '.#.', '#..'],
  '(': ['.#.', '#..', '#..', '#..', '.#.'],
  ')': ['.#.', '..#', '..#', '..#', '.#.'],
  '0': ['.#.', '#.#', '#.#', '#.#', '.#.'],
  '1': ['.#.', '##.', '.#.', '.#.', '###'],
  '2': ['##.', '..#', '.#.', '#..', '###'],
  '3': ['##.', '..#', '##.', '..#', '##.'],
  '4': ['#.#', '#.#', '###', '..#', '..#'],
  '5': ['###', '#..', '##.', '..#', '##.'],
  '6': ['.#.', '#..', '##.', '#.#', '.#.'],
  '7': ['###', '..#', '.#.', '.#.', '.#.'],
  '8': ['.#.', '#.#', '.#.', '#.#', '.#.'],
  '9': ['.#.', '#.#', '.##', '..#', '.#.'],
};

export function drawText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, color: string, scale = 1): void {
  ctx.fillStyle = color;
  let cx = x;
  const step = 4 * scale;
  for (const raw of text.toUpperCase()) {
    const glyph = FONT[raw];
    if (glyph) {
      for (let row = 0; row < glyph.length; row++) {
        for (let col = 0; col < glyph[row].length; col++) {
          if (glyph[row][col] === '#') ctx.fillRect(cx + col * scale, y + row * scale, scale, scale);
        }
      }
    }
    cx += step;
  }
}

function hash(x: number, y: number): number {
  let n = Math.imul(x, 374761393) + Math.imul(y, 668265263);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return (n ^ (n >>> 16)) >>> 0;
}

const ash = Array.from({ length: 28 }, (_, i) => ({
  x: (i * 97) % (MAP_W * TILE),
  y: (i * 53) % (DRAWN_H * TILE),
  s: 0.4 + (i % 5) * 0.15,
}));

/** Tiles as this person sees them. Past Halt, the Leak's near edge goes out for anyone who stopped the count. */
function seenTile(tx: number, ty: number, leakQuiet: boolean): number {
  const tile = tiles[ty][tx];
  if (leakQuiet && tile === T_GLOW && tx >= LEAK_EDGE.x0 && tx < LEAK_EDGE.x1 && ty >= LEAK_EDGE.y0 && ty < LEAK_EDGE.y1) return T_GRASS;
  return tile;
}

function drawTiles(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, time: number, leakQuiet = false): void {
  for (let ty = y0; ty < y1; ty++) {
    for (let tx = x0; tx < x1; tx++) {
      const tile = seenTile(tx, ty, leakQuiet);
      const x = tx * TILE;
      const y = ty * TILE;
      const n = hash(tx, ty);
      if (tile === T_DIRT) ctx.fillStyle = n % 5 === 0 ? '#7a624c' : '#6a5340';
      else if (tile === T_ROAD) ctx.fillStyle = n % 7 === 0 ? '#4a4a46' : '#3a3a38';
      else if (tile === T_WATER) ctx.fillStyle = Math.floor(time * 2 + tx) % 2 === 0 ? '#2f4f4c' : '#3d6662';
      else if (tile === T_DINER) ctx.fillStyle = (tx + ty) % 2 === 0 ? '#7a4038' : '#6a3832';
      else if (tile === T_STATION) ctx.fillStyle = '#6a6e68';
      else if (tile === T_COUNTER) ctx.fillStyle = '#6d5432';
      else if (tile === T_ROCK) ctx.fillStyle = hash(tx >> 1, ty >> 1) % 3 === 0 ? '#5e5a54' : hash(tx >> 1, ty >> 1) % 3 === 1 ? '#56524c' : '#4e4a44';
      else if (tile === T_CRUST) ctx.fillStyle = n % 2 === 0 ? '#9a9386' : '#7a7468';
      else if (tile === T_SALT) ctx.fillStyle = n % 5 === 0 ? '#e7e2d4' : '#d4cfc2';
      else if (tile === T_GLASS) ctx.fillStyle = n % 4 === 0 ? '#5a927c' : '#467a66';
      else if (tile === T_GREASE) ctx.fillStyle = n % 3 === 0 ? '#1e2418' : '#14180f';
      else if (tile === T_TRACK) ctx.fillStyle = n % 6 === 0 ? '#4a453c' : '#5c5648';
      else if (tile === T_STATIC) ctx.fillStyle = n % 4 === 0 ? '#8a9aa4' : '#6d7c86';
      else if (tile === T_COPPER) ctx.fillStyle = n % 2 === 0 ? '#6a4a32' : '#5a3e2c';
      else if (tile === T_MARROW) ctx.fillStyle = n % 4 === 0 ? '#d4c6b6' : '#c4b6a6';
      else if (tile === T_RIB) ctx.fillStyle = n % 2 === 0 ? '#8a8078' : '#6e6860';
      else if (tile === T_GLOW) ctx.fillStyle = n % 3 === 0 ? '#76a256' : '#628a46';
      else if (tile === T_TOXIC) ctx.fillStyle = Math.floor(time * 3 + tx) % 2 === 0 ? '#6a9a32' : '#3d6b22';
      else if (tile === T_PROP) ctx.fillStyle = tx >= OLD_W || ty >= OLD_H ? '#4a453c' : ty >= 16 ? '#3a3a38' : '#6a5340';
      else if (tile === T_ASH) ctx.fillStyle = n % 4 === 0 ? '#8e8a82' : '#7e7a72';
      else if (tile === T_SHARD) ctx.fillStyle = n % 2 === 0 ? '#9ad4c4' : '#7fbfa3';
      else if (tile === T_TREE) ctx.fillStyle = regionGround(tx, ty);
      else if (tile === T_SCRUB) ctx.fillStyle = tiles[ty][tx - 1] === T_GRASS || tiles[ty][tx + 1] === T_GRASS ? (n % 4 === 0 ? '#4a5640' : '#3c4934') : '#6a5340';
      else if (tile === T_VENT) ctx.fillStyle = '#6e6a62';
      else if (tile === T_FLOOR) ctx.fillStyle = (tx + ty) % 2 === 0 ? '#4a4844' : '#44423e';
      else if (tile === T_WALL) ctx.fillStyle = n % 3 === 0 ? '#34343a' : '#2c2c30';
      else if (tile === T_MUD) ctx.fillStyle = n % 3 === 0 ? '#5a4a34' : '#4e402e';
      else if (tile === T_BLANK) ctx.fillStyle = n % 5 === 0 ? '#e4dcc6' : '#e9e2cf';
      else if (tile === T_SKETCH) ctx.fillStyle = '#d8d0bb';
      else ctx.fillStyle = n % 4 === 0 ? '#4a5640' : '#3c4934';
      const tex =
        tile === T_WATER ? drawLiquid(ctx, 'water', x, y, TILE, time) : tile === T_TOXIC ? drawLiquid(ctx, 'toxic', x, y, TILE, time) : drawGroundTile(ctx, tile === T_TREE || tile === T_PROP ? -1 : tile, n, x, y, TILE);
      if (!tex) ctx.fillRect(x, y, TILE, TILE);
      if (tile === T_GRASS && n % 3 === 0 && !tex) {
        ctx.fillStyle = '#2c3426';
        ctx.fillRect(x + (n % 8), y + ((n >> 3) % 8), 2, 2);
      }
      if (tile === T_DIRT && n % 6 === 0 && !tex) {
        ctx.fillStyle = '#4e3d30';
        ctx.fillRect(x + (n % 10), y + ((n >> 4) % 8), 2, 1);
      }
      if (tile === T_ROAD && ty % 5 === 0 && tx % 4 === 0) {
        ctx.fillStyle = '#c4a15a';
        ctx.fillRect(x + 4, y + 7, 6, 2);
      }
      if (tile === T_WATER && n % 2 === 0) {
        ctx.fillStyle = 'rgba(232,236,230,0.25)';
        ctx.fillRect(x + ((n + Math.floor(time * 8)) % 10), y + 4, 3, 1);
      }
      if (tile === T_SALT && n % 3 === 0 && !tex) {
        ctx.fillStyle = '#a39886';
        ctx.fillRect(x + 2, y + (n % 10), 8, 1);
      }
      if (tile === T_GLASS && n % 2 === 0 && !tex) {
        ctx.fillStyle = 'rgba(244,241,234,0.35)';
        ctx.fillRect(x + 6, y + 2, 2, 10);
      }
      if (tile === T_GREASE && n % 2 === 0 && !tex) {
        ctx.fillStyle = 'rgba(127,191,163,0.25)';
        ctx.fillRect(x + 2, y + (n % 8), 7, 1);
      }
      if (tile === T_TRACK && n % 9 === 0 && !tex) {
        ctx.fillStyle = '#3a342c';
        ctx.fillRect(x + 3, y + 7, 8, 1);
      }
      if (tile === T_STATIC && n % 3 === 0 && !tex) {
        ctx.fillStyle = 'rgba(224,177,90,0.35)';
        ctx.fillRect(x + 7, y + 2, 1, 8);
      }
      if (tile === T_GLOW && n % 2 === 0) {
        ctx.fillStyle = 'rgba(190,220,120,0.45)';
        ctx.fillRect(x + 4, y + 4, 3, 3);
      }
      if (tile === T_TOXIC && n % 2 === 0 && !tex) {
        ctx.fillStyle = 'rgba(210,230,90,0.7)';
        ctx.fillRect(x + 3 + (n % 6), y + 4, 3, 2);
      }
      if (tile === T_MARROW && n % 4 === 0 && !tex) {
        ctx.fillStyle = '#8a8078';
        ctx.fillRect(x + 4, y + (n % 8), 5, 1);
      }
      if (tile === T_ASH && n % 5 === 0 && !tex) {
        ctx.fillStyle = '#5c5850';
        ctx.fillRect(x + (n % 9), y + ((n >> 4) % 10), 5, 1);
        ctx.fillRect(x + (n % 9) + 4, y + ((n >> 4) % 10), 1, 3);
      }
      if (tile === T_SCRUB) {
        ctx.fillStyle = '#8a7a44';
        ctx.fillRect(x + 3, y + 6, 2, 6);
        ctx.fillRect(x + 6, y + 4, 2, 8);
        ctx.fillRect(x + 9, y + 7, 2, 5);
        ctx.fillStyle = '#6a5a34';
        ctx.fillRect(x + 2, y + 11, 11, 2);
      }
      if (tile === T_VENT) {
        const puff = Math.floor(time * 3 + (n % 7)) % 3;
        ctx.fillStyle = '#2a241c';
        ctx.fillRect(x + 4, y + 5, 8, 6);
        ctx.fillStyle = '#e07a3d';
        ctx.fillRect(x + 6, y + 7, 4, 2);
        ctx.fillStyle = `rgba(235,228,212,${0.18 + puff * 0.1})`;
        ctx.fillRect(x + 5, y + 1 - puff, 5, 3);
      }
      if (tile === T_FLOOR && n % 7 === 0 && !tex) {
        ctx.fillStyle = '#3a3834';
        ctx.fillRect(x, y + 8, TILE, 1);
      }
      if (tile === T_MUD && n % 3 === 0 && !tex) {
        ctx.fillStyle = 'rgba(20,16,12,0.35)';
        ctx.fillRect(x + (n % 8), y + ((n >> 3) % 9), 5, 3);
      }
      if (tile === T_BLANK) {
        // Faint pencil guides where the painter meant to come back.
        ctx.fillStyle = 'rgba(106,100,88,0.18)';
        if (tx % 4 === 0) ctx.fillRect(x, y, 1, TILE);
        if (ty % 4 === 0) ctx.fillRect(x, y, TILE, 1);
        if (n % 9 === 0) {
          ctx.fillStyle = 'rgba(58,54,48,0.35)';
          ctx.fillRect(x + (n % 10) + 1, y + ((n >> 4) % 10) + 2, 4, 1);
        }
        // A painted neighbour bleeds a little way onto the paper.
        const left = tx > 0 ? tiles[ty][tx - 1] : T_BLANK;
        if (left !== T_BLANK && left !== T_SKETCH && left !== T_TRACK) {
          ctx.fillStyle = left === T_DIRT ? 'rgba(106,83,64,0.5)' : 'rgba(60,73,52,0.5)';
          for (let i = 0; i < 4; i++) ctx.fillRect(x, y + i * 4 + (n % 3), 2 + ((n >> i) % 4), 2);
        }
      }
      if (tile === T_SKETCH) {
        // Hatching, drawn and never inked.
        ctx.fillStyle = 'rgba(58,54,48,0.45)';
        for (let i = -TILE; i < TILE; i += 4) {
          for (let k = 0; k < TILE; k += 2) {
            const hx = i + k;
            if (hx >= 0 && hx < TILE) ctx.fillRect(x + hx, y + TILE - 1 - k, 1, 1);
          }
        }
      }
      if (tile === T_SHARD) {
        ctx.fillStyle = 'rgba(244,241,234,0.55)';
        ctx.fillRect(x + 4, y + 2, 2, 11);
        ctx.fillRect(x + 9, y + 5, 1, 8);
      }
      if (tile === T_ROCK && !tex) {
        // Stones in the stone, so a big mass reads as rock and not as a slab.
        ctx.fillStyle = '#6e6a63';
        ctx.fillRect(x + (n % 9) + 1, y + ((n >> 5) % 9) + 1, 4, 3);
        ctx.fillStyle = '#3e3a35';
        ctx.fillRect(x + (n % 9) + 1, y + ((n >> 5) % 9) + 4, 4, 1);
        if (n % 3 === 0) {
          ctx.fillStyle = '#3e3a35';
          ctx.fillRect(x + ((n >> 3) % 11) + 2, y + ((n >> 7) % 7) + 6, 1, 4);
          ctx.fillRect(x + ((n >> 3) % 11) + 2, y + ((n >> 7) % 7) + 9, 3, 1);
        }
      }
      if ((tile === T_ROCK || tile === T_CRUST || tile === T_WALL || tile === T_SHARD || tile === T_SKETCH) && ty + 1 < MAP_H && !isWall(tiles[ty + 1][tx]) && tiles[ty + 1][tx] !== T_TREE) {
        // The face you can see from the south, so walls stand up off the ground.
        const face =
          tile === T_CRUST ? ['#5c574e', '#46423b'] : tile === T_SHARD ? ['#4f8a78', '#3a6a5c'] : tile === T_WALL ? ['#1e1e22', '#141418'] : tile === T_SKETCH ? ['#a8a08c', '#6a6458'] : ['#34302b', '#29261f'];
        ctx.fillStyle = face[0];
        ctx.fillRect(x, y + TILE - 6, TILE, 4);
        ctx.fillStyle = face[1];
        ctx.fillRect(x, y + TILE - 2, TILE, 2);
      }
      if (tile === T_CRUST && n % 2 === 0 && !tex) {
        ctx.fillStyle = '#b4ad9e';
        ctx.fillRect(x + (n % 10) + 1, y + ((n >> 4) % 7) + 1, 4, 2);
      }
      if (isWall(tile)) drawWallEdge(ctx, tx, ty, tile);
      if (tile === T_COUNTER) {
        ctx.fillStyle = '#8a6a42';
        ctx.fillRect(x, y, TILE, 4);
      }
    }
  }
}

/** What a tree is standing in. */
function regionGround(tx: number, ty: number): string {
  if (ty >= 132 && tx < 190) return '#7e7a72';
  if (tx >= 190 && ty >= 38 && ty < 132) return '#6a5340';
  return hash(tx, ty) % 4 === 0 ? '#4a5640' : '#3c4934';
}

function drawWallEdge(ctx: CanvasRenderingContext2D, tx: number, ty: number, tile: number): void {
  const x = tx * TILE;
  const y = ty * TILE;
  const lip =
    tile === T_SHARD
      ? '#e7f4ee'
      : tile === T_WALL
        ? '#5c5c64'
        : tile === T_SKETCH
          ? '#3a3630'
        : tile === T_CRUST
      ? '#f4f1ea'
      : tile === T_COPPER
        ? '#c47a4a'
        : tile === T_RIB
          ? '#e7d3c4'
          : tile === T_DINER
            ? '#c47a6a'
            : tile === T_ROCK
              ? '#a39e96'
              : tile === T_COUNTER
                ? '#c4a574'
                : '#d5d8d2';
  const open = (dx: number, dy: number) => {
    const nx = tx + dx;
    const ny = ty + dy;
    if (nx < 0 || ny < 0 || nx >= MAP_W || ny >= MAP_H) return false;
    return !isWall(tiles[ny][nx]);
  };
  ctx.fillStyle = '#141210';
  if (open(0, -1)) ctx.fillRect(x, y, TILE, 2);
  if (open(0, 1)) ctx.fillRect(x, y + TILE - 2, TILE, 2);
  if (open(-1, 0)) ctx.fillRect(x, y, 2, TILE);
  if (open(1, 0)) ctx.fillRect(x + TILE - 2, y, 2, TILE);
  ctx.fillStyle = lip;
  if (open(0, -1)) ctx.fillRect(x + 2, y + 2, TILE - 4, 1);
  if (open(-1, 0)) ctx.fillRect(x + 2, y + 2, 1, TILE - 4);
}

function drawDecal(ctx: CanvasRenderingContext2D, kind: string, x: number, y: number, time: number): void {
  if (kind === 'billboard') {
    box(ctx, x + 8, y + 20, 3, 14, '#5c564e');
    box(ctx, x + 46, y + 20, 3, 14, '#5c564e');
    box(ctx, x, y, 58, 22, '#d7c7a4');
    drawText(ctx, 'RADVILLE', x + 4, y + 3, '#1a1612');
    drawText(ctx, 'POP. VARIES', x + 4, y + 12, '#7a4038');
    return;
  }
  if (
    kind === 'eat' ||
    kind === 'junk' ||
    kind === 'station' ||
    kind === 'well' ||
    kind === 'hum' ||
    kind === 'pan' ||
    kind === 'lamps' ||
    kind === 'cut' ||
    kind === 'mile' ||
    kind === 'court' ||
    kind === 'slow' ||
    kind === 'marrow' ||
    kind === 'lot' ||
    kind === 'halt' ||
    kind === 'leak'
  ) {
    const labels: Record<string, string> = {
      eat: 'EAT',
      junk: 'JUNK',
      well: 'NO SIP',
      hum: 'THE HUM',
      pan: 'THE PAN',
      lamps: 'LAMPS',
      cut: 'SOFT',
      mile: 'THE MILE',
      court: 'THE COURT',
      slow: 'SLOW',
      marrow: 'MARROW',
      lot: 'BACK LOT',
      halt: 'HALT',
      leak: 'THE LEAK',
      station: 'CLICK STATION',
    };
    const label = labels[kind] ?? 'CLICK STATION';
    const w = label.length * 4 + 8;
    box(ctx, x + 4, y + 14, 3, 10, '#5c564e');
    box(ctx, x, y, w, 14, kind === 'eat' ? '#a34532' : '#d7c7a4');
    drawText(ctx, label, x + 4, y + 4, kind === 'eat' ? '#f4f1ea' : '#1a1612');
    if (kind === 'station') {
      ctx.fillStyle = '#1a1612';
      ctx.fillRect(x + w + 2, y + 6, 6, 2);
      ctx.fillRect(x + w + 6, y + 4, 2, 6);
    }
    return;
  }
  if (kind === 'pump') {
    const foot = y + TILE;
    box(ctx, x + 3, foot - 16, 10, 16, '#5c564e');
    box(ctx, x + 5, foot - 14, 6, 4, '#7fbfb2');
    ctx.fillStyle = '#a34532';
    ctx.fillRect(x + 12, foot - 10, 4, 2);
    ctx.fillRect(x + 14, foot - 10, 2, 6);
    return;
  }
  if (kind === 'wreck') {
    const foot = y + TILE * 2;
    if (drawObject(ctx, 'wreck', hash(Math.round(x), Math.round(y)) >>> 2, x + 23, foot, false, hash(Math.round(y), Math.round(x)) % 2 === 0)) return;
    box(ctx, x + 2, foot - 12, 42, 10, '#6a3832');
    box(ctx, x + 16, foot - 18, 16, 8, '#7a4038');
    ctx.fillStyle = '#7fbfb2';
    ctx.fillRect(x + 18, foot - 16, 5, 4);
    ctx.fillRect(x + 25, foot - 16, 5, 4);
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(x + 6, foot - 4, 8, 4);
    ctx.fillRect(x + 30, foot - 4, 8, 4);
    ctx.fillStyle = '#3e4a32';
    ctx.fillRect(x + 36, foot - 20, 2, 6);
    ctx.fillRect(x + 34, foot - 22, 5, 3);
    return;
  }
  if (kind === 'cooler') {
    const foot = y + TILE;
    const glow = 0.45 + Math.sin(time * 4) * 0.25;
    box(ctx, x + 2, foot - 12, 12, 10, '#8a8478');
    ctx.fillStyle = `rgba(127,191,178,${glow.toFixed(2)})`;
    ctx.fillRect(x + 4, foot - 8, 8, 2);
    ctx.fillStyle = '#5c564e';
    ctx.fillRect(x + 7, foot - 14, 2, 3);
    return;
  }
  if (kind === 'clapper') {
    const foot = y + TILE;
    const glow = 0.4 + Math.sin(time * 5) * 0.35;
    box(ctx, x + 6, foot - 14, 3, 12, '#5c564e');
    box(ctx, x + 3, foot - 16, 9, 6, '#7fbfb2');
    ctx.fillStyle = `rgba(224,177,90,${glow.toFixed(2)})`;
    ctx.fillRect(x + 6, foot - 14, 3, 2);
    return;
  }
  if (kind === 'keel') {
    const foot = y + TILE;
    const glow = 0.4 + Math.sin(time * 3) * 0.25;
    box(ctx, x + 6, foot - 16, 3, 14, '#e7e0d2');
    ctx.fillStyle = `rgba(244,241,234,${glow.toFixed(2)})`;
    ctx.fillRect(x + 6, foot - 16, 3, 3);
    return;
  }
  if (kind === 'hiss') {
    const foot = y + TILE;
    const glow = 0.4 + Math.sin(time * 6) * 0.3;
    box(ctx, x + 6, foot - 14, 3, 12, '#7fbfb2');
    ctx.fillStyle = `rgba(224,177,90,${glow.toFixed(2)})`;
    ctx.fillRect(x + 4, foot - 16, 3, 3);
    ctx.fillRect(x + 9, foot - 16, 3, 3);
    return;
  }
  if (kind === 'tooth') {
    const foot = y + TILE;
    box(ctx, x + 6, foot - 14, 4, 12, '#e7d3c4');
    ctx.fillStyle = '#a34532';
    ctx.fillRect(x + 7, foot - 8, 2, 2);
    return;
  }
  if (kind === 'cabinet') {
    const foot = y + TILE;
    box(ctx, x + 1, foot - 18, 14, 16, '#6d5432');
    box(ctx, x + 3, foot - 16, 4, 10, '#3a3228');
    box(ctx, x + 9, foot - 16, 4, 10, '#3a3228');
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(x + 7, foot - 12, 2, 2);
    drawText(ctx, 'YOURS', x - 2, foot - 22, '#e7d7c8');
    return;
  }
  if (kind === 'cushion') {
    const foot = y + TILE;
    box(ctx, x + 2, foot - 6, 12, 5, '#6d5432');
    ctx.fillStyle = '#a34532';
    ctx.fillRect(x + 4, foot - 8, 8, 3);
    return;
  }
  if (kind === 'mast') {
    const foot = y + TILE;
    box(ctx, x + 6, foot - 18, 3, 16, '#5c564e');
    box(ctx, x, foot - 20, 16, 5, '#7fbfb2');
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(x + 6, foot - 18, 4, 2);
    return;
  }
  if (kind === 'crate') {
    const foot = y + TILE;
    box(ctx, x + 2, foot - 12, 12, 12, '#8a6a42');
    ctx.fillStyle = '#4e3d30';
    ctx.fillRect(x + 2, foot - 7, 12, 1);
    ctx.fillRect(x + 7, foot - 12, 1, 12);
  }
}

function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string): void {
  ctx.fillStyle = '#1a1612';
  ctx.fillRect(Math.round(x) - 1, Math.round(y) - 1, w + 2, h + 2);
  ctx.fillStyle = fill;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

function labelFor(actor: DrawActor): { text: string; color: string; lift: number } {
  const lift =
    actor.kind === 'npc'
      ? 52
      : actor.kind === 'dog'
        ? 34
        : actor.kind === 'roach' ||
            actor.kind === 'fastroach' ||
            actor.kind === 'newt' ||
            actor.kind === 'skip' ||
            actor.kind === 'slick' ||
            actor.kind === 'mite' ||
            actor.kind === 'tick' ||
            actor.kind === 'drifter' ||
            actor.kind === 'sleeper'
          ? 22
          : actor.kind === 'toad' || actor.kind === 'slug' || actor.kind === 'panback' || actor.kind === 'crab' || actor.kind === 'leaker'
            ? 28
            : actor.kind === 'filament' || actor.kind === 'nail'
              ? 36
              : actor.kind === 'mast' || actor.kind === 'drawer' || actor.kind === 'count'
                ? 34
                : 40;
  const big = actor.kind === 'kettle' || actor.kind === 'master' || actor.kind === 'foreman' || actor.kind === 'wanderer' || actor.kind === 'surveyor' || actor.kind === 'mainspring' || actor.kind === 'lastnumber';
  const tall = actor.kind === 'walker' || actor.kind === 'clerk' || actor.kind === 'moth';
  const color = actor.kind === 'npc' ? '#e0b15a' : actor.kind === 'player' ? '#f4f1ea' : actor.boss ? '#e07a3d' : actor.champ ? '#e0b15a' : '#e7d7c8';
  return { text: actor.name, color, lift: big ? (actor.kind === 'kettle' ? 34 : 54) : tall ? 34 : actor.kind === 'hound' ? 22 : lift };
}

export type WorldView = {
  cssW: number;
  cssH: number;
  dpr: number;
  scale: number;
  camX: number;
  camY: number;
  time: number;
  actors: DrawActor[];
  shots: DrawShot[];
  fx: FxSnap[];
  floats: { x: number; y: number; text: string; color: string; age: number }[];
  nodes: { id: string; left: number }[];
  drops: DropSnap[];
  fires: { id: number; x: number; y: number; mine: boolean; home: boolean }[];
  guide: { x: number; y: number } | null;
  /** Where the story is waiting. Gets a small pulsing mark. */
  storySpot: { x: number; y: number } | null;
  stairOpen: boolean;
  /** The west end of the road goes somewhere, for this person. */
  edgeOpen: boolean;
  /** This person stopped the count: the Leak's near edge is out. */
  leakQuiet: boolean;
  knownMarks: string[];
  opened: string[];
  looted: Set<string>;
  aimX: number;
  aimY: number;
  showAim: boolean;
  minimap: { x: number; y: number } | null;
  others: { id: number; name: string; x: number; y: number }[];
  youId: number;
  event: { x: number; y: number; name: string } | null;
  labels: boolean;
  dropLabels: Map<number, LabelBox>;
  hoverDrop: number | null;
  reduce: boolean;
};

const SIGN_KINDS = new Set(['billboard', 'eat', 'junk', 'station', 'well', 'hum', 'pan', 'lamps', 'cut', 'mile', 'court', 'slow', 'marrow', 'lot', 'halt', 'leak']);

export function drawWorld(ctx: CanvasRenderingContext2D, opts: WorldView): void {
  const viewW = opts.cssW / opts.scale;
  const viewH = opts.cssH / opts.scale;
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  ctx.fillStyle = '#141210';
  ctx.fillRect(0, 0, opts.cssW, opts.cssH);
  ctx.setTransform(opts.dpr * opts.scale, 0, 0, opts.dpr * opts.scale, -opts.camX * opts.dpr * opts.scale, -opts.camY * opts.dpr * opts.scale);
  ctx.imageSmoothingEnabled = false;

  const x0 = Math.max(0, Math.floor(opts.camX / TILE) - 1);
  const y0 = Math.max(0, Math.floor(opts.camY / TILE) - 1);
  const x1 = Math.min(MAP_W, Math.ceil((opts.camX + viewW) / TILE) + 1);
  const y1 = Math.min(MAP_H, Math.ceil((opts.camY + viewH) / TILE) + 2);
  drawTiles(ctx, x0, y0, x1, y1, opts.reduce ? 0 : opts.time, opts.leakQuiet);

  const pad = 48;
  const inView = (x: number, y: number) => x > opts.camX - pad && x < opts.camX + viewW + pad && y > opts.camY - pad && y < opts.camY + viewH + pad * 1.5;

  const fx = sceneFx(opts, inView);
  stepFx(opts.time, fx.emitters, opts.reduce);
  drawGlow(ctx, opts.reduce ? fx.glows.map((glow) => ({ ...glow, alpha: glow.alpha * 0.45 })) : fx.glows);

  if (!opts.reduce) {
    ctx.fillStyle = 'rgba(235,228,212,0.35)';
    for (const mote of ash) {
      mote.y -= mote.s * 0.35;
      mote.x += 0.08;
      if (mote.y < opts.camY - 20) mote.y = opts.camY + viewH + 10;
      if (mote.x > opts.camX + viewW + 20) mote.x = opts.camX - 10;
      if (mote.x < opts.camX - 40 || mote.y > opts.camY + viewH + 40) {
        mote.x = opts.camX + Math.random() * viewW;
        mote.y = opts.camY + Math.random() * viewH;
      }
      ctx.fillRect(Math.round(mote.x), Math.round(mote.y), 1, 1);
    }
  }

  type Layer = { y: number; draw: () => void };
  const layers: Layer[] = [];
  // Trees stand up out of their tile.
  for (let ty = y0; ty < y1; ty++) {
    for (let tx = x0; tx < x1; tx++) {
      if (tiles[ty][tx] !== T_TREE) continue;
      layers.push({ y: ty * TILE + TILE, draw: () => drawTree(ctx, tx * TILE, ty * TILE, tx, ty) });
    }
  }
  const nodeLeft = new Map(opts.nodes.map((node) => [node.id, node.left]));
  for (const node of ALL_NODES) {
    if (!inView(node.x, node.y)) continue;
    const left = nodeLeft.get(node.id);
    if (left === undefined || left <= 0) continue;
    layers.push({ y: node.y, draw: () => drawHeap(ctx, node.stuff, node.x, node.y) });
  }
  for (const box of BOXES) {
    if (!inView(box.x, box.y)) continue;
    const face = box.face ?? 'crate';
    const empty = opts.opened.includes(box.id);
    if (face === 'crate') {
      const painted = DECALS.some((decal) => decal.kind === 'crate' && decal.x + TILE / 2 === box.x && decal.y + TILE / 2 === box.y);
      if (painted) continue;
      layers.push({ y: box.y, draw: () => drawCrate(ctx, box.x, box.y, empty) });
    } else if (face === 'barrel') {
      layers.push({ y: box.y, draw: () => drawBarrel(ctx, box.x, box.y, empty) });
    } else {
      layers.push({ y: box.y, draw: () => drawDecal(ctx, 'wreck', box.x - 20, box.y - TILE, opts.time) });
    }
  }
  for (const cache of GEN.caches) {
    if (!inView(cache.x, cache.y)) continue;
    const empty = opts.looted.has(cache.id);
    layers.push({ y: cache.y, draw: () => drawCache(ctx, cache, empty, opts.time) });
  }
  for (const poi of GEN.pois) {
    if (poi.kind !== 'wreck') continue;
    const wx = (poi.tx - 1) * TILE;
    const wy = (poi.ty - 2) * TILE;
    if (!inView(wx + 24, wy + 24)) continue;
    layers.push({ y: wy + TILE * 2, draw: () => drawDecal(ctx, 'wreck', wx, wy, opts.time) });
  }
  for (const prop of GEN.props) {
    if (!inView(prop.x, prop.y)) continue;
    if (prop.kind === 'sign') continue;
    layers.push({ y: prop.y, draw: () => drawProp(ctx, prop.kind, prop.x, prop.y, opts.time) });
  }
  if (opts.edgeOpen && inView(EDGE.out.x, EDGE.out.y)) layers.push({ y: EDGE.out.y - 40, draw: () => drawTornEdge(ctx, EDGE.out.x, EDGE.out.y, opts.time) });
  if (opts.stairOpen && inView(STAIRS.down.x, STAIRS.down.y)) layers.push({ y: STAIRS.down.y - 8, draw: () => drawProp(ctx, 'stair', STAIRS.down.x, STAIRS.down.y, opts.time) });
  for (const town of TOWNS) {
    if (inView(town.post.x, town.post.y)) layers.push({ y: town.post.y, draw: () => drawPost(ctx, town.post.x, town.post.y) });
    if (town.id !== 'radville' && inView(town.locker.x, town.locker.y)) layers.push({ y: town.locker.y, draw: () => drawDecal(ctx, 'cabinet', town.locker.x - 8, town.locker.y - 8, opts.time) });
  }
  for (const drop of opts.drops) {
    layers.push({ y: drop.y, draw: () => drawDrop(ctx, drop, opts.time) });
  }
  for (const fire of opts.fires) {
    layers.push({ y: fire.y, draw: () => drawFire(ctx, fire.x, fire.y, opts.reduce ? 0 : opts.time + fire.id, fire.home) });
  }
  for (const prop of DECALS) {
    if (SIGN_KINDS.has(prop.kind)) continue;
    if (!inView(prop.x, prop.y)) continue;
    const fh = (prop.kind === 'wreck' ? 2 : 1) * TILE;
    layers.push({ y: prop.y + fh, draw: () => drawDecal(ctx, prop.kind, prop.x, prop.y, opts.time) });
  }
  for (const actor of opts.actors) {
    if (!inView(actor.x, actor.y)) continue;
    layers.push({ y: actor.y, draw: () => drawActor(ctx, actor, opts) });
  }
  layers.sort((a, b) => a.y - b.y);
  for (const layer of layers) layer.draw();
  if (!opts.reduce) drawParticles(ctx);
  for (const sign of DECALS) if (SIGN_KINDS.has(sign.kind) && inView(sign.x, sign.y)) drawDecal(ctx, sign.kind, sign.x, sign.y, opts.time);
  for (const prop of GEN.props) if (prop.kind === 'sign' && inView(prop.x, prop.y)) drawSign(ctx, prop.text ?? '', prop.x, prop.y);

  for (const shot of opts.shots) drawShot(ctx, shot);
  drawCombatFx(ctx, opts.fx, opts.time);
  if (opts.storySpot && inView(opts.storySpot.x, opts.storySpot.y)) {
    const bob = Math.round(Math.sin(opts.time * 3) * 2);
    const sx = Math.round(opts.storySpot.x);
    const sy = Math.round(opts.storySpot.y - 34 + bob);
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(sx - 3, sy - 1, 7, 9);
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(sx - 2, sy, 5, 5);
    ctx.fillRect(sx - 1, sy + 6, 3, 1);
  }

  if (opts.minimap) {
    // Night. Glowing tiles in view are light sources too.
    const lights = fx.glows.slice();
    for (const shot of opts.shots) {
      if (!inView(shot.x, shot.y)) continue;
      const rgb = shotGlow(shot);
      if (rgb) lights.push({ x: shot.x, y: shot.y, rx: 3, ry: 3, rgb, alpha: 0.1, blend: 'lighter' });
    }
    for (const b of boomLights()) {
      if (b.look === 'dust') continue;
      const rgb = BOOM_RGB[b.look] ?? BOOM_RGB.fire;
      lights.push({ x: b.x, y: b.y, rx: b.r * 0.5, ry: b.r * 0.5, rgb, alpha: 0.2 * b.fade, blend: 'lighter' });
    }
    let count = 0;
    for (let ty = y0; ty < y1 && count < 150; ty++) {
      for (let tx = x0; tx < x1 && count < 150; tx++) {
        if (tiles[ty][tx] !== T_GLOW || (opts.leakQuiet && tx >= LEAK_EDGE.x0 && tx < LEAK_EDGE.x1 && ty >= LEAK_EDGE.y0 && ty < LEAK_EDGE.y1)) continue;
        lights.push({ x: tx * TILE + TILE / 2, y: ty * TILE + TILE / 2, rx: 8, ry: 8, rgb: [143, 191, 106], alpha: 0.08, blend: 'lighter' });
        count++;
      }
    }
    drawDarkness(ctx, { dpr: opts.dpr, scale: opts.scale, camX: opts.camX, camY: opts.camY, time: opts.time, reduce: opts.reduce, you: opts.minimap }, lights);
  }

  // Loot names float above everything else so they can be read and clicked.
  for (const drop of opts.drops) {
    const box = opts.dropLabels.get(drop.id);
    if (box) drawDropLabel(ctx, drop, box, opts.hoverDrop === drop.id);
  }

  for (const floater of opts.floats) {
    const rise = floater.age * 18;
    const w = floater.text.length * 4;
    drawText(ctx, floater.text, floater.x - w / 2 + 1, floater.y - rise + 1, '#1a1612');
    drawText(ctx, floater.text, floater.x - w / 2, floater.y - rise, floater.color);
  }

  if (opts.showAim) {
    ctx.fillStyle = '#f4f1ea';
    const mx = Math.round(opts.aimX);
    const my = Math.round(opts.aimY);
    ctx.fillRect(mx - 4, my, 2, 1);
    ctx.fillRect(mx + 3, my, 2, 1);
    ctx.fillRect(mx, my - 4, 1, 2);
    ctx.fillRect(mx, my + 3, 1, 2);
  }

  if (opts.minimap) drawMinimap(ctx, opts, opts.minimap);
  if (opts.guide) drawGuide(ctx, opts, opts.guide);
}

const ENEMY_DRAW: Partial<Record<EnemyKind, (ctx: CanvasRenderingContext2D, x: number, y: number, pose: Pose, time: number, flash: boolean) => void>> = {
  toad: drawToad,
  newt: drawNewt,
  slug: drawSlug,
  panback: drawPanback,
  skip: drawSkip,
  filament: drawFilament,
  slick: drawSlick,
  drifter: drawDrifter,
  sleeper: drawSleeper,
  mite: drawMite,
  nail: drawNail,
  mast: drawMast,
  tick: drawTick,
  crab: drawCrab,
  drawer: drawDrawer,
  leaker: drawLeaker,
  count: drawCount,
  hound: drawHound,
  walker: drawWalker,
  moth: drawMoth,
  clerk: drawClerk,
  kettle: drawKettle,
  master: drawMaster,
  foreman: drawForeman,
  blot: drawBlot,
  surveyor: drawSurveyor,
  mainspring: drawMainspring,
  lastnumber: drawLastNumber,
  wanderer: drawWanderer,
};

// A hint of a walk: walkers hop a pixel or two per step and waddle side to side.
// rate = steps per second (matches the leg flicker), hop = world px, tilt = radians.
type Gait = { rate: number; hop: number; tilt: number };
const GAIT_PERSON: Gait = { rate: 8, hop: 1.6, tilt: 0.06 };
const GAIT_DEFAULT: Gait = { rate: 6, hop: 1.4, tilt: 0.07 };
const GAIT: Partial<Record<DrawActor['kind'], Gait>> = {
  dog: { rate: 9, hop: 1.6, tilt: 0.05 },
  roach: { rate: 10, hop: 0.8, tilt: 0.05 },
  fastroach: { rate: 10, hop: 0.8, tilt: 0.05 },
  mite: { rate: 12, hop: 0.8, tilt: 0.05 },
  tick: { rate: 12, hop: 0.8, tilt: 0.05 },
  newt: { rate: 12, hop: 0.6, tilt: 0.09 },
  crab: { rate: 9, hop: 0.8, tilt: 0.08 },
  toad: { rate: 3, hop: 3.4, tilt: 0.04 },
  skip: { rate: 4, hop: 3, tilt: 0.05 },
  slug: { rate: 3, hop: 0, tilt: 0.05 },
  slick: { rate: 3, hop: 0, tilt: 0.05 },
  blot: { rate: 3, hop: 0.6, tilt: 0.06 },
  moth: { rate: 5, hop: 1.2, tilt: 0.1 },
  kettle: { rate: 3, hop: 1, tilt: 0.05 },
  master: { rate: 3, hop: 1, tilt: 0.04 },
  foreman: { rate: 3, hop: 1, tilt: 0.05 },
  mainspring: { rate: 3, hop: 1, tilt: 0.04 },
  lastnumber: { rate: 3, hop: 1, tilt: 0.04 },
  surveyor: { rate: 3, hop: 1, tilt: 0.04 },
  wanderer: { rate: 4, hop: 1.2, tilt: 0.05 },
};

function drawActor(ctx: CanvasRenderingContext2D, actor: DrawActor, opts: WorldView): void {
  const bob = opts.reduce || actor.kind === 'player' ? 0 : Math.sin(opts.time * 2 + actor.id) * 0.6;
  const y = actor.y + bob;
  // Each walker gets its own step timing so a pack doesn't march in lockstep.
  // Legs use the same shifted clock, so feet and hops stay in sync.
  if (actor.champ && !opts.reduce) {
    // Loud ones hum. You can see it before you hear it.
    const pulse = 0.18 + Math.sin(opts.time * 5 + actor.id) * 0.08;
    ctx.fillStyle = `rgba(224,177,90,${pulse.toFixed(2)})`;
    ctx.fillRect(Math.round(actor.x - 9), Math.round(y - 2), 18, 4);
  }
  const sprite = hasChar(spriteSlug(actor));
  const walking = actor.pose === 'walk' && !actor.dead && !opts.reduce && !sprite;
  const time = walking && actor.kind !== 'player' ? opts.time + ((actor.id * 0.618) % 1) : opts.time;
  if (walking) {
    const gait = GAIT[actor.kind] ?? (actor.kind === 'player' || actor.kind === 'npc' ? GAIT_PERSON : GAIT_DEFAULT);
    const phase = time * gait.rate * Math.PI;
    const lift = Math.round(Math.abs(Math.sin(phase)) * gait.hop);
    const tilt = Math.sin(phase) * gait.tilt;
    gaitGround.m = ctx.getTransform();
    gaitGround.lift = lift;
    ctx.save();
    ctx.translate(actor.x, y);
    ctx.rotate(tilt);
    ctx.translate(-actor.x, -y - lift);
    drawBody(ctx, actor, y, time);
    ctx.restore();
    gaitGround.m = null;
    gaitGround.lift = 0;
  } else {
    drawBody(ctx, actor, y, time);
  }
  drawOverhead(ctx, actor, y, opts);
}

/** Reskin: humanoid enemies that wear a Meshy-animated sheet (char-<slug>.png). The rest keep their drawing. */
const ENEMY_SPRITE: Partial<Record<string, string>> = {
  drifter: 'zombie_feral_infected',
  sleeper: 'zombie_hospital_infected',
  walker: 'zombie_armored_zombie',
  clerk: 'zombie_police_officer',
  foreman: 'zombie_construction_zombie',
};

function spriteSlug(actor: DrawActor): string | null {
  if (actor.kind === 'player' || actor.kind === 'npc') return spriteFor(actor.npc ?? 'player', actor.look);
  return ENEMY_SPRITE[actor.kind] ?? null;
}

function drawBody(ctx: CanvasRenderingContext2D, actor: DrawActor, y: number, time: number): void {
  const enemySlug = actor.kind !== 'player' && actor.kind !== 'npc' ? ENEMY_SPRITE[actor.kind] : undefined;
  if (enemySlug && hasChar(enemySlug)) {
    ctx.fillStyle = 'rgba(12,10,8,0.35)';
    ctx.fillRect(Math.round(actor.x - 6), Math.round(y - 2), 12, 3);
    drawChar(ctx, enemySlug, actor.id, actor.x, y, actor.aim, actor.pose, Boolean(actor.dead), Boolean(actor.flash));
    return;
  }
  if (actor.kind === 'player' || actor.kind === 'npc') {
    drawPerson(ctx, actor.x, y, {
      look: actor.look,
      role: actor.npc ?? 'player',
      pose: actor.pose,
      aim: actor.aim,
      time: time,
      weapon: actor.weapon,
      origin: actor.origin,
      flash: actor.flash,
      dead: actor.dead,
      id: actor.id,
    });
  } else if (actor.kind === 'roach' || actor.kind === 'fastroach') {
    drawRoach(ctx, actor.x, y, actor.kind === 'fastroach', actor.pose, time, Boolean(actor.flash));
  } else if (actor.kind === 'dog') {
    drawDog(ctx, actor.x, y, actor.pose, time);
  } else {
    const draw = ENEMY_DRAW[actor.kind];
    if (draw) draw(ctx, actor.x, y, actor.pose, time, Boolean(actor.flash));
  }
}

function drawOverhead(ctx: CanvasRenderingContext2D, actor: DrawActor, y: number, opts: WorldView): void {
  if (actor.dead) return;
  const label = labelFor(actor);
  const text = label.text.toUpperCase();
  const width = text.length * 4;
  drawText(ctx, text, Math.round(actor.x - width / 2) + 1, Math.round(y - label.lift) + 1, '#1a1612');
  drawText(ctx, text, Math.round(actor.x - width / 2), Math.round(y - label.lift), label.color);
  if (actor.stun) {
    const blink = Math.floor(opts.time * 12) % 2 === 0;
    ctx.fillStyle = blink ? '#7fbfb2' : '#f4f1ea';
    ctx.fillRect(Math.round(actor.x) - 5, Math.round(y - 22), 2, 2);
    ctx.fillRect(Math.round(actor.x) + 3, Math.round(y - 24), 2, 2);
  }
  if (actor.maxHp > 0 && (actor.hp < actor.maxHp || actor.kind === 'player' || actor.boss)) {
    const bw = actor.boss ? 40 : actor.champ ? 24 : 16;
    const bx = Math.round(actor.x - bw / 2);
    const by = Math.round(y - label.lift + 7);
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(bx - 1, by - 1, bw + 2, 4);
    ctx.fillStyle = actor.kind === 'player' ? '#e07a3d' : actor.boss ? '#a34532' : actor.champ ? '#e0b15a' : '#c4a15a';
    ctx.fillRect(bx, by, Math.max(0, Math.round((bw * actor.hp) / actor.maxHp)), 2);
  }
}

type DrawShot = { id?: number; x: number; y: number; vx: number; vy: number; team: 'player' | 'enemy'; style?: ShotStyle; src?: string };

// Projectile art (public/art/shots.png, from art-src/shoots). Looks are family-colour:
// c- = fat comet blobs (cans, spit, boss lobs), t- = small tracer slugs (plain guns),
// b- = streaky bolts (charged/electric guns, fast casters).
const PLAYER_SHOT: Record<string, string> = {
  zip: 't-steel', // cheap pipe gun: a grey slug
  prod: 'c-ice', // a rude blue spark
  pop: 'c-ember',
  coil: 'b-cyan', // storm gutter
  bell: 'c-fire',
  suck: 'c-pink',
  scatter: 't-orange',
  rod: 't-green', // leak rod
  rifle: 't-gold',
  humbox: 'b-rainbow', // five bad notes
  prong: 'b-white', // static
  nought: 'c-shadow', // round and empty
  signal: 'b-red',
  mastbolt: 'b-gold',
};
const ENEMY_SHOT: Record<string, string> = {
  toad: 'c-acid',
  slug: 'b-green',
  panback: 't-red',
  nail: 'b-white',
  walker: 't-steel',
  clerk: 'b-blood',
  mast: 'b-gold',
  drawer: 'c-fire',
  count: 'b-violet',
  kettle: 'c-ember',
  master: 'b-cyan',
  foreman: 't-orange',
  surveyor: 'b-red',
  mainspring: 't-gold',
  lastnumber: 'c-shadow',
  wanderer: 'c-pink',
};
/** Enemies carrying one of the player guns (Prod Drifter, Pop Crab, ...) shoot what it shoots. */
const STYLE_SHOT: Record<string, string> = { stun: 'c-ice', pop: 'c-ember', bell: 'c-fire', suck: 'c-pink' };
const BIG_SHOTS = new Set(['mast', 'drawer', 'count', 'kettle', 'master', 'foreman', 'surveyor', 'mainspring', 'lastnumber', 'wanderer']);
/** Dark looks still need to show at night: give them a coloured halo instead of their own black. */
const DARK_GLOW: Record<string, [number, number, number]> = { 'c-shadow': [150, 110, 180], 'b-blood': [190, 50, 70] };

function shotLook(shot: DrawShot): string {
  const style = shot.style ?? 'bolt';
  if (shot.team === 'player') return (shot.src && PLAYER_SHOT[shot.src]) || STYLE_SHOT[style] || 't-gold';
  if (style !== 'bolt') return STYLE_SHOT[style] ?? 'c-acid';
  return (shot.src && ENEMY_SHOT[shot.src]) || 'c-acid';
}

function shotGlow(shot: DrawShot): [number, number, number] | null {
  const look = shotLook(shot);
  return DARK_GLOW[look] ?? shotRgb(look);
}

function drawShot(ctx: CanvasRenderingContext2D, shot: DrawShot): void {
  const look = shotLook(shot);
  const k = (look.startsWith('t-') ? 1 : 0.9) * (shot.team === 'enemy' && shot.src && BIG_SHOTS.has(shot.src) ? 1.3 : 1);
  if (drawShotArt(ctx, look, shot.id ?? 0, shot.x, shot.y, shot.vx, shot.vy, k)) return;
  const style = shot.style ?? 'bolt';
  if (shot.team === 'enemy') {
    ctx.fillStyle = '#b6c46a';
    ctx.fillRect(Math.round(shot.x) - 2, Math.round(shot.y) - 2, 4, 4);
  } else if (style === 'stun') {
    ctx.fillStyle = '#7fbfb2';
    ctx.fillRect(Math.round(shot.x) - 2, Math.round(shot.y) - 2, 4, 4);
    ctx.fillStyle = '#f4f1ea';
    ctx.fillRect(Math.round(shot.x) - 1, Math.round(shot.y) - 4, 2, 2);
  } else if (style === 'pop') {
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(Math.round(shot.x) - 4, Math.round(shot.y) - 4, 8, 8);
    ctx.fillStyle = '#e07a3d';
    ctx.fillRect(Math.round(shot.x) - 3, Math.round(shot.y) - 3, 6, 6);
  } else if (style === 'bell') {
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(Math.round(shot.x) - 4, Math.round(shot.y) - 4, 8, 8);
    ctx.fillStyle = '#e7d7a4';
    ctx.fillRect(Math.round(shot.x) - 3, Math.round(shot.y) - 3, 6, 6);
  } else if (style === 'suck') {
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(Math.round(shot.x) - 4, Math.round(shot.y) - 4, 8, 8);
    ctx.fillStyle = '#b48cdc';
    ctx.fillRect(Math.round(shot.x) - 3, Math.round(shot.y) - 3, 6, 6);
  } else {
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(Math.round(shot.x) - 2, Math.round(shot.y) - 2, 4, 4);
  }
  ctx.fillStyle = 'rgba(20,16,12,0.45)';
  ctx.fillRect(Math.round(shot.x - shot.vx * 0.02), Math.round(shot.y - shot.vy * 0.02), 2, 2);
}

/** Blast art per fx kind (art.ts drawBooms); suck plays the swirl backwards so it inhales. */
const BOOM_LOOK: Partial<Record<FxSnap['kind'], string>> = { pop: 'fire', bell: 'blue', suck: 'swirl', death: 'dust' };
const BOOM_RGB: Record<string, [number, number, number]> = { fire: [240, 150, 60], big: [250, 170, 70], small: [240, 140, 60], blue: [120, 200, 240], swirl: [170, 140, 230] };

function drawCombatFx(ctx: CanvasRenderingContext2D, fx: FxSnap[], time: number): void {
  for (const item of fx) {
    if (item.kind === 'death' && item.boss) {
      noteBossBoom(item.id, item.x, item.y, Math.max(14, item.r));
      continue;
    }
    const look = BOOM_LOOK[item.kind];
    if (look) noteBoom(item.id, look, item.x, item.y, item.kind === 'death' ? Math.max(8, item.r * 0.9) : Math.max(12, item.r * 0.85), item.kind === 'suck');
  }
  drawBooms(ctx);
  for (const item of fx) {
    if (item.kind === 'laser' && item.x0 != null && item.y0 != null) {
      const steps = Math.max(1, Math.ceil(Math.hypot(item.x - item.x0, item.y - item.y0) / 3));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const px = item.x0 + (item.x - item.x0) * t;
        const py = item.y0 + (item.y - item.y0) * t;
        ctx.fillStyle = i % 3 === 0 ? '#f4f1ea' : '#7fbfb2';
        ctx.fillRect(Math.round(px) - 1, Math.round(py) - 1, 2, 2);
      }
    }
    if (item.kind === 'spark') {
      ctx.fillStyle = '#7fbfb2';
      ctx.fillRect(Math.round(item.x) - 2, Math.round(item.y) - 2, 4, 4);
    }
    if (item.kind === 'pop' || item.kind === 'bell' || item.kind === 'suck') {
      const color = item.kind === 'pop' ? '#e07a3d' : item.kind === 'bell' ? '#e7d7a4' : '#b48cdc';
      const radius = Math.max(8, item.r);
      ctx.fillStyle = color;
      for (let i = 0; i < 18; i++) {
        const angle = (Math.PI * 2 * i) / 18;
        ctx.fillRect(Math.round(item.x + Math.cos(angle) * radius) - 1, Math.round(item.y + Math.sin(angle) * radius) - 1, 2, 2);
      }
      if (item.kind === 'suck') {
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI * 2 * i) / 6 + time * 3;
          ctx.fillRect(Math.round(item.x + Math.cos(angle) * radius * 0.45) - 1, Math.round(item.y + Math.sin(angle) * radius * 0.45) - 1, 2, 2);
        }
      }
    }
  }
}

function sceneFx(opts: { time: number; actors: DrawActor[]; fires: { id: number; x: number; y: number }[]; drops: DropSnap[] }, inView: (x: number, y: number) => boolean): { glows: GlowSpec[]; emitters: EmitSpec[] } {
  const allGlows: GlowSpec[] = [];
  const allEmitters: EmitSpec[] = [];
  const glows = { push: (g: GlowSpec) => (inView(g.x, g.y) ? allGlows.push(g) : 0) };
  const emitters = { push: (e: EmitSpec) => (inView(e.x, e.y) ? allEmitters.push(e) : 0) };
  for (const drop of opts.drops) {
    if (!drop.rarity || drop.rarity === 'plain') continue;
    const rgb: [number, number, number] = drop.rarity === 'odd' ? [127, 191, 178] : drop.rarity === 'wrong' ? [224, 177, 90] : [224, 122, 61];
    glows.push({ x: drop.x, y: drop.y - 3, rx: 7, ry: 4, rgb, alpha: 0.22, blend: 'lighter' });
    if (drop.rarity !== 'odd') emitters.push({ id: `drop${drop.id}`, x: drop.x, y: drop.y - 4, rate: 6, life: 0.9, speed: 2, rise: 18, spread: 0.3, rgb, size: 1, blend: 'lighter' });
  }
  for (const actor of opts.actors) {
    if (actor.dead || !actor.boss) continue;
    glows.push({ x: actor.x, y: actor.y - 12, rx: 16, ry: 9, rgb: actor.kind === 'wanderer' ? [180, 140, 220] : actor.kind === 'master' ? [127, 191, 178] : [224, 122, 61], alpha: 0.14, blend: 'lighter' });
  }
  for (const prop of GEN.props) {
    if (prop.kind === 'stone') glows.push({ x: prop.x, y: prop.y - 10, rx: 9, ry: 11, rgb: [224, 177, 90], alpha: 0.1, blend: 'lighter' });
    if (prop.kind === 'lamppost') glows.push({ x: prop.x, y: prop.y - 20, rx: 22, ry: 14, rgb: [224, 177, 90], alpha: 0.12, blend: 'lighter' });
    if (prop.kind === 'kettle') emitters.push({ id: `kettle${prop.x}`, x: prop.x, y: prop.y - 6, rate: 5, life: 1.4, speed: 3, rise: 14, spread: 1.4, rgb: [235, 228, 212], size: 2, blend: 'source-over' });
  }

  for (const fire of opts.fires) {
    glows.push({ x: fire.x, y: fire.y - 4, rx: 11, ry: 6, rgb: [224, 122, 61], alpha: 0.18, blend: 'lighter' });
    emitters.push({ id: `fire${fire.id}`, x: fire.x, y: fire.y - 7, rate: 8, life: 0.65, speed: 8, rise: 16, spread: 1.2, rgb: [224, 150, 72], size: 2, blend: 'lighter' });
  }
  for (const actor of opts.actors) {
    if (actor.dead) continue;
    if (actor.kind === 'filament' || actor.kind === 'nail') {
      const rgb: [number, number, number] = actor.kind === 'nail' ? [224, 177, 90] : [127, 191, 163];
      glows.push({ x: actor.x, y: actor.y - 14, rx: 4, ry: 7, rgb, alpha: 0.14, blend: 'lighter' });
      emitters.push({ id: `fil${actor.id}`, x: actor.x, y: actor.y - 16, rate: 5, life: 0.8, speed: 4, rise: 10, spread: 0.8, rgb, size: 2, blend: 'lighter' });
    }
    if (actor.kind === 'mast' || actor.kind === 'drawer' || actor.kind === 'count') {
      const rgb: [number, number, number] = actor.kind === 'count' ? [143, 191, 106] : actor.kind === 'drawer' ? [224, 122, 61] : [127, 191, 178];
      glows.push({ x: actor.x, y: actor.y - 12, rx: 10, ry: 6, rgb, alpha: 0.12, blend: 'lighter' });
    }
  }
  glows.push({ x: KEEL.x, y: KEEL.y - 10, rx: 5, ry: 8, rgb: [244, 241, 234], alpha: 0.12, blend: 'lighter' });
  emitters.push({ id: 'keel', x: KEEL.x, y: KEEL.y - 12, rate: 3, life: 0.9, speed: 2, rise: 6, spread: 0.5, rgb: [244, 241, 234], size: 1, blend: 'lighter' });
  glows.push({ x: HISS.x, y: HISS.y - 10, rx: 5, ry: 7, rgb: [127, 191, 178], alpha: 0.14, blend: 'lighter' });
  emitters.push({ id: 'hiss', x: HISS.x, y: HISS.y - 12, rate: 3, life: 0.7, speed: 3, rise: 8, spread: 0.6, rgb: [224, 177, 90], size: 1, blend: 'lighter' });
  glows.push({ x: TOOTH.x, y: TOOTH.y - 8, rx: 4, ry: 6, rgb: [224, 122, 61], alpha: 0.1, blend: 'lighter' });
  // Area dust. source-over so pale salt and black grease do not blow out.
  emitters.push({ id: 'lamps', x: 8 * TILE + 8, y: 6 * TILE + 8, rate: 4, life: 1.1, speed: 4, rise: 8, spread: 2, rgb: [160, 220, 190], size: 2, blend: 'lighter' });
  emitters.push({ id: 'cut', x: 16 * TILE + 8, y: 56 * TILE + 8, rate: 3, life: 1.2, speed: 4, rise: 6, spread: 2.2, rgb: [90, 180, 140], size: 2, blend: 'lighter' });
  emitters.push({ id: 'pan', x: 84 * TILE + 8, y: 32 * TILE + 8, rate: 4, life: 1.3, speed: 5, rise: 4, spread: 2.4, rgb: [244, 236, 210], size: 2, blend: 'lighter' });
  emitters.push({ id: 'court', x: 200 * TILE, y: 32 * TILE, rate: 4, life: 1.1, speed: 4, rise: 8, spread: 2.2, rgb: [180, 220, 230], size: 2, blend: 'lighter' });
  emitters.push({ id: 'marrow', x: 20 * TILE, y: 120 * TILE, rate: 3, life: 1.2, speed: 3, rise: 6, spread: 2, rgb: [230, 190, 150], size: 2, blend: 'lighter' });
  emitters.push({ id: 'leak', x: 340 * TILE, y: 34 * TILE, rate: 6, life: 1.3, speed: 4, rise: 8, spread: 2.6, rgb: [170, 220, 90], size: 2, blend: 'lighter' });
  glows.push({ x: 340 * TILE, y: 34 * TILE, rx: 28, ry: 18, rgb: [143, 191, 106], alpha: 0.08, blend: 'lighter' });
  return { glows: allGlows, emitters: allEmitters };
}

function drawHeap(ctx: CanvasRenderingContext2D, kind: string, x: number, y: number): void {
  if (drawObject(ctx, 'heap_' + kind, Math.round(x + y), x, y + 1)) return;
  const stuff = kind in STUFF ? STUFF[kind as keyof typeof STUFF] : null;
  ctx.fillStyle = 'rgba(12,10,8,0.35)';
  ctx.fillRect(Math.round(x - 5), Math.round(y - 2), 10, 3);
  if (kind === 'bulb' || stuff?.id === 'bulb') {
    box(ctx, x - 2, y - 8, 4, 6, '#7fbfb2');
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 10), 2, 2);
    return;
  }
  if (kind === 'wire') {
    ctx.strokeStyle = '#e0b15a';
    ctx.lineWidth = 1;
    ctx.strokeRect(Math.round(x - 4), Math.round(y - 7), 8, 5);
    return;
  }
  if (kind === 'rag') {
    box(ctx, x - 4, y - 6, 8, 4, '#a34532');
    return;
  }
  if (kind === 'bandage') {
    box(ctx, x - 5, y - 6, 10, 4, '#d7c7a4');
    ctx.fillStyle = '#a34532';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 6), 2, 4);
    return;
  }
  if (kind === 'stim') {
    box(ctx, x - 3, y - 9, 6, 8, '#3d6662');
    ctx.fillStyle = '#f4f1ea';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 8), 2, 4);
    ctx.fillRect(Math.round(x - 2), Math.round(y - 7), 4, 2);
    return;
  }
  if (kind === 'camp') {
    box(ctx, x - 4, y - 6, 8, 5, '#5c564e');
    ctx.fillStyle = '#e07a3d';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 8), 2, 2);
    return;
  }
  if (kind === 'grit') {
    box(ctx, x - 3, y - 7, 3, 3, '#f4f1ea');
    box(ctx, x + 1, y - 5, 3, 3, '#e7e0d2');
    return;
  }
  if (kind === 'slag' || stuff?.id === 'slag') {
    box(ctx, x - 4, y - 6, 8, 5, '#5a7a42');
    ctx.fillStyle = '#d7e46a';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 8), 2, 2);
    return;
  }
  if (kind === 'drip') {
    box(ctx, x - 4, y - 5, 8, 4, '#1a1814');
    ctx.fillStyle = '#3f6b5a';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 4), 3, 1);
    return;
  }
  box(ctx, x - 5, y - 6, 10, 5, '#8a8478');
}

function drawFire(ctx: CanvasRenderingContext2D, x: number, y: number, time: number, home: boolean): void {
  const flick = Math.round(Math.sin(time * 9) * 1);
  box(ctx, x - 6, y - 3, 12, 4, home ? '#8a6a32' : '#5c564e');
  box(ctx, x - 2, y - 9 - flick, 4, 6, '#e07a3d');
  ctx.fillStyle = '#e0b15a';
  ctx.fillRect(Math.round(x - 1), Math.round(y - 12 - flick), 2, 3);
  if (home) {
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(Math.round(x - 7), Math.round(y - 5), 2, 2);
    ctx.fillRect(Math.round(x + 5), Math.round(y - 5), 2, 2);
  }
}

function drawBarrel(ctx: CanvasRenderingContext2D, x: number, y: number, empty = false): void {
  if (drawObject(ctx, 'barrel', 0, x, y, empty)) return;
  ctx.fillStyle = 'rgba(12,10,8,0.35)';
  ctx.fillRect(Math.round(x - 6), Math.round(y - 2), 12, 3);
  box(ctx, x - 5, y - 12, 10, 10, empty ? '#4a2c28' : '#6a3832');
  ctx.fillStyle = empty ? '#6d5a44' : '#c4a574';
  ctx.fillRect(Math.round(x - 5), Math.round(y - 8), 10, 2);
  if (empty) {
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(Math.round(x - 3), Math.round(y - 12), 6, 2);
  }
}

function drawCrate(ctx: CanvasRenderingContext2D, x: number, y: number, empty = false): void {
  if (drawObject(ctx, empty ? 'crate_empty' : 'crate', 0, x, y)) return;
  box(ctx, x - 6, y - 10, 12, 10, empty ? '#4e3d30' : '#6d5432');
  ctx.fillStyle = empty ? '#1a1612' : '#e0b15a';
  if (empty) ctx.fillRect(Math.round(x - 5), Math.round(y - 10), 10, 2);
  else ctx.fillRect(Math.round(x - 1), Math.round(y - 6), 2, 2);
}

function drawCache(ctx: CanvasRenderingContext2D, cache: Cache, empty: boolean, time: number): void {
  if (cache.face === 'barrel') return drawBarrel(ctx, cache.x, cache.y, empty);
  if (cache.face === 'crate' || cache.face === 'wreck') return drawCrate(ctx, cache.x, cache.y, empty);
  if (drawObject(ctx, 'chest', 0, cache.x, cache.y, empty)) return;
  // A chest: bigger, banded, and it glints while it is full.
  ctx.fillStyle = 'rgba(12,10,8,0.35)';
  ctx.fillRect(Math.round(cache.x - 9), Math.round(cache.y - 2), 18, 3);
  box(ctx, cache.x - 8, cache.y - 12, 16, 11, empty ? '#4e3d30' : '#7a4a28');
  ctx.fillStyle = empty ? '#3a3228' : '#c4a574';
  ctx.fillRect(Math.round(cache.x - 8), Math.round(cache.y - 8), 16, 2);
  ctx.fillRect(Math.round(cache.x - 5), Math.round(cache.y - 12), 2, 11);
  ctx.fillRect(Math.round(cache.x + 3), Math.round(cache.y - 12), 2, 11);
  if (!empty && Math.floor(time * 2) % 3 === 0) {
    ctx.fillStyle = '#f4f1ea';
    ctx.fillRect(Math.round(cache.x + 5), Math.round(cache.y - 11), 1, 1);
  }
}

const RARITY_TINT: Record<Rarity, string> = RARITY_COLOR;

function drawDrop(ctx: CanvasRenderingContext2D, drop: DropSnap, time: number): void {
  if (drop.kind === 'tabs') {
    ctx.fillStyle = 'rgba(12,10,8,0.35)';
    ctx.fillRect(Math.round(drop.x - 4), Math.round(drop.y - 1), 8, 2);
    const n = Math.min(4, 1 + Math.floor(drop.n / 6));
    for (let i = 0; i < n; i++) box(ctx, drop.x - 4 + i * 2, drop.y - 4 - (i % 2) * 2, 4, 3, '#e0b15a');
    return;
  }
  if (!drop.rarity) return drawHeap(ctx, drop.kind, drop.x, drop.y);
  const tint = RARITY_TINT[drop.rarity];
  const bob = Math.round(Math.sin(time * 3 + drop.id) * 1);
  if (drop.rarity !== 'plain') {
    // A thin light straight up, so good things can be found from across a room.
    const a = drop.rarity === 'odd' ? 0.14 : 0.24;
    ctx.fillStyle = drop.rarity === 'odd' ? `rgba(127,191,178,${a})` : drop.rarity === 'wrong' ? `rgba(224,177,90,${a})` : `rgba(224,122,61,${a})`;
    ctx.fillRect(Math.round(drop.x - 1), Math.round(drop.y - 40), 2, 36);
  }
  ctx.fillStyle = 'rgba(12,10,8,0.35)';
  ctx.fillRect(Math.round(drop.x - 5), Math.round(drop.y - 1), 10, 3);
  box(ctx, drop.x - 4, drop.y - 7 + bob, 8, 5, '#5c564e');
  ctx.fillStyle = tint;
  ctx.fillRect(Math.round(drop.x - 4), Math.round(drop.y - 7 + bob), 8, 1);
  ctx.fillRect(Math.round(drop.x - 1), Math.round(drop.y - 6 + bob), 2, 3);
  if (drop.held) {
    ctx.fillStyle = 'rgba(20,16,12,0.45)';
    ctx.fillRect(Math.round(drop.x - 5), Math.round(drop.y - 8 + bob), 10, 7);
  }
}

function dropText(drop: DropSnap): string {
  if (drop.name) return drop.name.toUpperCase();
  if (drop.kind === 'tabs') return `${drop.n} TABS`;
  return (drop.kind in STUFF ? STUFF[drop.kind as keyof typeof STUFF].name : drop.kind).toUpperCase();
}

export type LabelBox = { x: number; y: number; w: number; h: number };

/**
 * Where every shown loot label sits, in world pixels, pushed apart so none overlap.
 * The client uses the same boxes to know what you clicked.
 */
export function layoutDropLabels(drops: DropSnap[], all: boolean, hover: number | null): Map<number, LabelBox> {
  const out = new Map<number, LabelBox>();
  const shown = drops.filter((drop) => (drop.rarity && drop.rarity !== 'plain') || all || drop.id === hover);
  // Better things claim their spot first.
  const rank = (drop: DropSnap) => (drop.rarity === 'relic' ? 0 : drop.rarity === 'wrong' ? 1 : drop.rarity === 'odd' ? 2 : 3);
  shown.sort((a, b) => rank(a) - rank(b) || a.y - b.y || a.x - b.x);
  const placed: LabelBox[] = [];
  for (const drop of shown) {
    const w = dropText(drop).length * 4 + 4;
    const box = { x: Math.round(drop.x - w / 2), y: Math.round(drop.y - 19), w, h: 9 };
    for (let tries = 0; tries < 30; tries++) {
      const hit = placed.find((other) => box.x < other.x + other.w + 1 && box.x + box.w + 1 > other.x && box.y < other.y + other.h + 1 && box.y + box.h + 1 > other.y);
      if (!hit) break;
      box.y = hit.y - box.h - 1;
    }
    placed.push(box);
    out.set(drop.id, box);
  }
  return out;
}

function drawDropLabel(ctx: CanvasRenderingContext2D, drop: DropSnap, label: LabelBox, hover: boolean): void {
  const text = dropText(drop);
  ctx.fillStyle = hover ? 'rgba(58,50,40,0.95)' : 'rgba(20,16,12,0.85)';
  ctx.fillRect(label.x, label.y, label.w, label.h);
  if (label.y < drop.y - 20) {
    // A thread back down to the thing it names.
    ctx.fillStyle = 'rgba(235,228,212,0.25)';
    ctx.fillRect(Math.round(drop.x), label.y + label.h, 1, Math.max(0, drop.y - 10 - label.y - label.h));
  }
  drawText(ctx, text, label.x + 2, label.y + 2, drop.held ? '#6d6458' : drop.rarity ? RARITY_TINT[drop.rarity] : drop.kind === 'tabs' ? '#e0b15a' : '#e7d7c8');
}

function drawTree(ctx: CanvasRenderingContext2D, x: number, y: number, tx: number, ty: number): void {
  if (drawObject(ctx, 'tree', hash(tx, ty) >>> 3, x + 8, y + 15, false, hash(tx, ty) % 2 === 0)) return;
  const n = hash(tx, ty);
  const lean = (n % 3) - 1;
  ctx.fillStyle = 'rgba(12,10,8,0.35)';
  ctx.fillRect(x + 2, y + 12, 12, 3);
  box(ctx, x + 6 + lean, y - 8, 4, 22, '#3a2e24');
  ctx.fillStyle = '#3a2e24';
  ctx.fillRect(x + 2 + lean, y - 4, 5, 2);
  ctx.fillRect(x + 9 + lean, y - 10, 5, 2);
  ctx.fillRect(x + 1 + lean, y - 12, 2, 8);
  ctx.fillRect(x + 13 + lean, y - 16, 2, 7);
  if (n % 4 === 0) {
    ctx.fillStyle = '#5e7a45';
    ctx.fillRect(x + 11 + lean, y - 18, 4, 3);
  }
}

function drawProp(ctx: CanvasRenderingContext2D, kind: string, x: number, y: number, time: number): void {
  if (['tent', 'deadfire', 'bones', 'stone', 'bench'].includes(kind) && drawObject(ctx, kind, hash(Math.round(x), Math.round(y)) >>> 2, x, y + 1)) return;
  if (kind === 'deadfire') {
    box(ctx, x - 6, y - 3, 12, 3, '#3a3228');
    ctx.fillStyle = '#5c564e';
    ctx.fillRect(Math.round(x - 3), Math.round(y - 5), 6, 2);
    return;
  }
  if (kind === 'tent') {
    ctx.fillStyle = 'rgba(12,10,8,0.35)';
    ctx.fillRect(Math.round(x - 12), Math.round(y - 1), 24, 3);
    ctx.fillStyle = '#1a1612';
    ctx.beginPath();
    ctx.moveTo(x - 12, y);
    ctx.lineTo(x, y - 16);
    ctx.lineTo(x + 12, y);
    ctx.fill();
    ctx.fillStyle = '#6d5432';
    ctx.beginPath();
    ctx.moveTo(x - 10, y - 1);
    ctx.lineTo(x, y - 14);
    ctx.lineTo(x + 10, y - 1);
    ctx.fill();
    ctx.fillStyle = '#2a241c';
    ctx.fillRect(Math.round(x - 2), Math.round(y - 7), 4, 6);
    return;
  }
  if (kind === 'bones') {
    ctx.fillStyle = '#d4c6b6';
    ctx.fillRect(Math.round(x - 5), Math.round(y - 2), 10, 2);
    ctx.fillRect(Math.round(x - 6), Math.round(y - 3), 2, 4);
    ctx.fillRect(Math.round(x + 4), Math.round(y - 3), 2, 4);
    return;
  }
  if (kind === 'lamppost') {
    box(ctx, x - 1, y - 26, 2, 26, '#3a3228');
    box(ctx, x - 3, y - 30, 6, 4, '#e0b15a');
    return;
  }
  if (kind === 'pylon') {
    box(ctx, x - 1, y - 40, 3, 40, '#5c564e');
    box(ctx, x - 10, y - 38, 21, 2, '#5c564e');
    box(ctx, x - 7, y - 30, 15, 2, '#5c564e');
    ctx.fillStyle = Math.floor(time * 1.5) % 2 ? '#e07058' : '#5a2a24';
    ctx.fillRect(Math.round(x), Math.round(y - 42), 2, 2);
    return;
  }
  if (kind === 'stair') {
    // A dark hole with steps going into it.
    ctx.fillStyle = '#0c0a08';
    ctx.fillRect(Math.round(x - 7), Math.round(y - 7), 14, 13);
    ctx.fillStyle = '#3a3228';
    for (let i = 0; i < 4; i++) ctx.fillRect(Math.round(x - 6 + i), Math.round(y - 6 + i * 3), 12 - i * 2, 2);
    return;
  }
  if (kind === 'gear') {
    const turn = Math.floor(time * 2) % 2;
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(Math.round(x - 7), Math.round(y - 14), 14, 14);
    ctx.fillStyle = '#c47a4a';
    ctx.fillRect(Math.round(x - 6), Math.round(y - 13), 12, 12);
    ctx.fillStyle = '#6a4a32';
    ctx.fillRect(Math.round(x - 2), Math.round(y - 9), 4, 4);
    ctx.fillStyle = '#e0b15a';
    if (turn) {
      ctx.fillRect(Math.round(x - 1), Math.round(y - 16), 2, 3);
      ctx.fillRect(Math.round(x - 1), Math.round(y - 1), 2, 2);
    } else {
      ctx.fillRect(Math.round(x - 9), Math.round(y - 8), 3, 2);
      ctx.fillRect(Math.round(x + 6), Math.round(y - 8), 3, 2);
    }
    return;
  }
  if (kind === 'coach') {
    // The last coach: long, dark green, brass trim, every window lit.
    ctx.fillStyle = 'rgba(12,10,8,0.4)';
    ctx.fillRect(Math.round(x - 30), Math.round(y - 1), 60, 4);
    box(ctx, x - 30, y - 30, 60, 26, '#2f4a3a');
    box(ctx, x - 32, y - 34, 64, 5, '#1e2a22');
    ctx.fillStyle = '#c4a15a';
    ctx.fillRect(Math.round(x - 30), Math.round(y - 12), 60, 2);
    const flick = Math.floor(time * 2) % 5;
    for (let i = 0; i < 5; i++) {
      ctx.fillStyle = i === flick ? '#f4e2a8' : '#e0b15a';
      ctx.fillRect(Math.round(x - 26 + i * 11), Math.round(y - 26), 7, 8);
    }
    box(ctx, x - 24, y - 5, 8, 5, '#1a1612');
    box(ctx, x + 16, y - 5, 8, 5, '#1a1612');
    return;
  }
  if (kind === 'clock') {
    // On the pillar's face. No hands.
    box(ctx, x - 7, y - 28, 14, 14, '#efeae0');
    ctx.fillStyle = '#3a3228';
    for (const [dx, dy] of [
      [0, -6],
      [5, 0],
      [0, 5],
      [-6, 0],
    ])
      ctx.fillRect(Math.round(x + dx), Math.round(y - 21 + dy), 1, 1);
    ctx.fillRect(Math.round(x), Math.round(y - 21), 1, 1);
    return;
  }
  if (kind === 'buffer') {
    box(ctx, x - 6, y - 14, 12, 10, '#a34532');
    box(ctx, x - 8, y - 12, 3, 3, '#3a3228');
    box(ctx, x + 5, y - 12, 3, 3, '#3a3228');
    return;
  }
  if (kind === 'bench') {
    ctx.fillStyle = 'rgba(12,10,8,0.35)';
    ctx.fillRect(Math.round(x - 10), Math.round(y - 1), 20, 3);
    box(ctx, x - 10, y - 8, 20, 3, '#6d5432');
    box(ctx, x - 9, y - 5, 2, 5, '#3a3228');
    box(ctx, x + 7, y - 5, 2, 5, '#3a3228');
    box(ctx, x - 10, y - 13, 20, 2, '#6d5432');
    return;
  }
  if (kind === 'stone') {
    ctx.fillStyle = 'rgba(12,10,8,0.35)';
    ctx.fillRect(Math.round(x - 7), Math.round(y - 2), 14, 3);
    box(ctx, x - 5, y - 18, 10, 17, '#6d7c86');
    ctx.fillStyle = '#8a9aa4';
    ctx.fillRect(Math.round(x - 3), Math.round(y - 16), 3, 12);
    const hum = Math.floor(time * 4) % 4;
    ctx.fillStyle = '#e0b15a';
    ctx.fillRect(Math.round(x - 1), Math.round(y - 12 + hum), 2, 2);
    return;
  }
  if (kind === 'kettle') {
    // The kettle's own pit. The thing itself is a monster.
    ctx.fillStyle = 'rgba(20,16,12,0.45)';
    ctx.fillRect(Math.round(x - 20), Math.round(y - 6), 40, 12);
    ctx.fillStyle = '#e07a3d';
    ctx.fillRect(Math.round(x - 14), Math.round(y - 1), 28, 2);
  }
}

function drawPost(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  box(ctx, x - 1, y - 20, 3, 20, '#5c564e');
  box(ctx, x - 13, y - 26, 28, 9, '#d7c7a4');
  drawText(ctx, 'COACH', x - 10, y - 24, '#1a1612');
}

function drawSign(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
  // Out in the Margin, some signs never got their words.
  const w = text ? text.length * 4 + 8 : 26;
  box(ctx, x + 4, y + 14, 3, 10, '#5c564e');
  box(ctx, x, y, w, 14, '#d7c7a4');
  drawText(ctx, text, x + 4, y + 4, '#1a1612');
}

/** The west end of the road, once it goes somewhere: the ground runs out like a torn page. */
function drawTornEdge(ctx: CanvasRenderingContext2D, x: number, y: number, time: number): void {
  const left = 0;
  for (let i = 0; i < 12; i++) {
    const ry = y - 48 + i * 8;
    const reach = 5 + ((i * 7) % 5) + (Math.floor(time * 2 + i) % 3 === 0 ? 1 : 0);
    ctx.fillStyle = '#e9e2cf';
    ctx.fillRect(left, ry, reach, 8);
    ctx.fillStyle = 'rgba(58,54,48,0.5)';
    ctx.fillRect(left + reach, ry + 1, 1, 6);
  }
  void x;
}

function drawGuide(
  ctx: CanvasRenderingContext2D,
  opts: { cssW: number; cssH: number; dpr: number; scale: number; camX: number; camY: number },
  guide: { x: number; y: number },
): void {
  const sx = (guide.x - opts.camX) * opts.scale;
  const sy = (guide.y - opts.camY) * opts.scale;
  const pad = 28;
  const inside = sx > pad && sy > pad && sx < opts.cssW - pad && sy < opts.cssH - pad;
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  ctx.fillStyle = '#e0b15a';
  if (inside) {
    ctx.fillRect(sx - 2, sy - 14, 4, 4);
    ctx.fillRect(sx - 1, sy - 10, 2, 6);
    return;
  }
  const cx = opts.cssW / 2;
  const cy = opts.cssH / 2;
  const ang = Math.atan2(sy - cy, sx - cx);
  const ex = Math.max(pad, Math.min(opts.cssW - pad, cx + Math.cos(ang) * (opts.cssW / 2 - pad)));
  const ey = Math.max(pad, Math.min(opts.cssH - pad, cy + Math.sin(ang) * (opts.cssH / 2 - pad)));
  ctx.save();
  ctx.translate(ex, ey);
  ctx.rotate(ang);
  ctx.beginPath();
  ctx.moveTo(8, 0);
  ctx.lineTo(-6, 5);
  ctx.lineTo(-6, -5);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Maps. The whole country is painted once, one pixel a tile; fog is laid over it
// from the explored chunks and redone only when that changes.

const CHUNK = 8;
const CHUNK_W = Math.ceil(MAP_W / CHUNK);
let groundMap: HTMLCanvasElement | null = null;
let fogMap: HTMLCanvasElement | null = null;
let seenBits: Uint8Array | null = null;

function tileColor(tile: number): string {
  if (tile === T_WATER) return '#3d6662';
  if (tile === T_ROAD || tile === T_TRACK || tile === T_PROP) return '#6a6254';
  if (tile === T_DIRT || tile === T_SCRUB) return '#6a5340';
  if (tile === T_SALT) return '#d7d2c4';
  if (tile === T_GLASS) return '#5f9b85';
  if (tile === T_GREASE) return '#1e2418';
  if (tile === T_STATIC) return '#6d7c86';
  if (tile === T_MARROW) return '#b4a696';
  if (tile === T_GLOW) return '#6a8a48';
  if (tile === T_TOXIC) return '#3d6b22';
  if (tile === T_ASH || tile === T_VENT) return '#8a867e';
  if (tile === T_FLOOR) return '#4a4844';
  if (tile === T_MUD) return '#54452f';
  if (tile === T_TREE) return '#2a241c';
  if (tile === T_SHARD) return '#a8dccb';
  if (tile === T_GRASS) return '#3c4934';
  if (tile === T_BLANK) return '#e9e2cf';
  if (tile === T_SKETCH) return '#8a8474';
  return '#1a1612';
}

function ensureGround(): HTMLCanvasElement {
  if (groundMap) return groundMap;
  const canvas = document.createElement('canvas');
  canvas.width = MAP_W;
  canvas.height = MAP_H;
  const g = canvas.getContext('2d');
  if (g) {
    const img = g.createImageData(MAP_W, MAP_H);
    const cache = new Map<number, [number, number, number]>();
    for (let ty = 0; ty < MAP_H; ty++) {
      for (let tx = 0; tx < MAP_W; tx++) {
        const tile = tiles[ty][tx];
        let rgb = cache.get(tile);
        if (!rgb) {
          const hex = tileColor(tile);
          rgb = [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
          cache.set(tile, rgb);
        }
        const i = (ty * MAP_W + tx) * 4;
        img.data[i] = rgb[0];
        img.data[i + 1] = rgb[1];
        img.data[i + 2] = rgb[2];
        img.data[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
  }
  groundMap = canvas;
  return canvas;
}

/** New explored chunks from the server. */
export function setSeen(bits: Uint8Array): void {
  seenBits = bits;
  const ground = ensureGround();
  const canvas = fogMap ?? document.createElement('canvas');
  canvas.width = MAP_W;
  canvas.height = MAP_H;
  const g = canvas.getContext('2d');
  if (!g) return;
  g.drawImage(ground, 0, 0);
  g.fillStyle = 'rgba(16,14,12,0.86)';
  for (let cy = 0; cy * CHUNK < MAP_H; cy++) {
    for (let cx = 0; cx < CHUNK_W; cx++) {
      if (chunkSeen(cx, cy)) continue;
      g.fillRect(cx * CHUNK, cy * CHUNK, CHUNK, CHUNK);
    }
  }
  fogMap = canvas;
}

export function chunkSeen(cx: number, cy: number): boolean {
  if (!seenBits) return false;
  const bit = cy * CHUNK_W + cx;
  return (seenBits[bit >> 3] & (1 << (bit & 7))) !== 0;
}

export function seenAt(wx: number, wy: number): boolean {
  return chunkSeen(Math.floor(wx / TILE / CHUNK), Math.floor(wy / TILE / CHUNK));
}

export const MINI = { w: 184, h: 124, pad: 12 };

function drawMinimap(ctx: CanvasRenderingContext2D, opts: WorldView, center: { x: number; y: number }): void {
  const map = fogMap ?? ensureGround();
  const { w, h, pad } = MINI;
  const x = opts.cssW - w - pad;
  const y = pad;
  const zoom = 2;
  const spanX = w / zoom;
  const spanY = h / zoom;
  let sx = center.x / TILE - spanX / 2;
  let sy = center.y / TILE - spanY / 2;
  sx = Math.max(0, Math.min(MAP_W - spanX, sx));
  sy = Math.max(0, Math.min(MAP_H - spanY, sy));
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  ctx.fillStyle = 'rgba(20,16,12,0.9)';
  ctx.fillRect(x - 3, y - 3, w + 6, h + 6);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(map, sx, sy, spanX, spanY, x, y, w, h);
  ctx.strokeStyle = '#3a3228';
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 2, y - 2, w + 4, h + 4);
  const toX = (wx: number) => x + (wx / TILE - sx) * zoom;
  const toY = (wy: number) => y + (wy / TILE - sy) * zoom;
  const dot = (wx: number, wy: number, color: string, size = 3) => {
    const px = toX(wx);
    const py = toY(wy);
    if (px < x || py < y || px > x + w || py > y + h) return;
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(px - size / 2 - 1, py - size / 2 - 1, size + 2, size + 2);
    ctx.fillStyle = color;
    ctx.fillRect(px - size / 2, py - size / 2, size, size);
  };
  for (const town of TOWNS) dot(town.post.x, town.post.y, '#e0b15a', 4);
  for (const mark of MARKS) if (opts.knownMarks.includes(mark.id)) dot(mark.x, mark.y, opts.guide && Math.abs(opts.guide.x - mark.x) < 2 && Math.abs(opts.guide.y - mark.y) < 2 ? '#e0b15a' : '#7fbfb2');
  for (const fire of opts.fires) dot(fire.x, fire.y, fire.home ? '#e07a3d' : '#8a4a32');
  for (const actor of opts.actors) {
    if (actor.dead || actor.kind === 'player' || actor.kind === 'npc' || actor.kind === 'dog') continue;
    dot(actor.x, actor.y, actor.boss ? '#e07a3d' : actor.champ ? '#e0b15a' : '#a34532', actor.boss ? 4 : 2);
  }
  for (const drop of opts.drops) if (drop.rarity && drop.rarity !== 'plain') dot(drop.x, drop.y, RARITY_TINT[drop.rarity], 2);
  if (opts.event) dot(opts.event.x, opts.event.y, '#b48cdc', 5);
  for (const other of opts.others) dot(other.x, other.y, other.id === opts.youId ? '#f4f1ea' : '#7fbfb2', other.id === opts.youId ? 4 : 3);
}

/** The big map (M). Fits the whole country into the box. */
export function drawWorldMap(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  opts: {
    you: { x: number; y: number } | null;
    others: { id: number; name: string; x: number; y: number }[];
    youId: number;
    knownMarks: string[];
    guideId: string | null;
    event: { x: number; y: number; name: string } | null;
    jobs: { x: number; y: number }[];
    fires: { x: number; y: number; home: boolean }[];
  },
): { scale: number; ox: number; oy: number } {
  const map = fogMap ?? ensureGround();
  // Only the drawn country is on the map. The places past it are not, by definition.
  const scale = Math.min(w / MAP_W, h / DRAWN_H);
  const ox = (w - MAP_W * scale) / 2;
  const oy = (h - DRAWN_H * scale) / 2;
  ctx.fillStyle = '#0c0a08';
  ctx.fillRect(0, 0, w, h);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(map, 0, 0, MAP_W, DRAWN_H, ox, oy, MAP_W * scale, DRAWN_H * scale);
  const toX = (wx: number) => ox + (wx / TILE) * scale;
  const toY = (wy: number) => oy + (wy / TILE) * scale;
  ctx.font = '11px Georgia, serif';
  ctx.textBaseline = 'middle';
  const label = (text: string, wx: number, wy: number, color: string) => {
    if (wy >= DRAWN_H * TILE) return;
    const px = toX(wx);
    const py = toY(wy);
    ctx.fillStyle = 'rgba(12,10,8,0.7)';
    const tw = ctx.measureText(text).width;
    ctx.fillRect(px + 5, py - 7, tw + 6, 14);
    ctx.fillStyle = color;
    ctx.fillText(text, px + 8, py);
  };
  const pin = (wx: number, wy: number, color: string, size = 5) => {
    if (wy >= DRAWN_H * TILE) return;
    ctx.fillStyle = '#1a1612';
    ctx.fillRect(toX(wx) - size / 2 - 1, toY(wy) - size / 2 - 1, size + 2, size + 2);
    ctx.fillStyle = color;
    ctx.fillRect(toX(wx) - size / 2, toY(wy) - size / 2, size, size);
  };
  for (const poi of GEN.pois) {
    if (poi.kind === 'town' || poi.kind === 'lair') continue;
    if (!seenAt(poi.tx * TILE, poi.ty * TILE)) continue;
    pin(poi.tx * TILE, poi.ty * TILE, poi.kind === 'den' ? '#a34532' : poi.kind === 'camp' ? '#c4a15a' : '#8a8478', 3);
  }
  for (const mark of MARKS) {
    if (!opts.knownMarks.includes(mark.id)) continue;
    const on = opts.guideId === mark.id;
    pin(mark.x, mark.y, on ? '#e0b15a' : '#7fbfb2', on ? 7 : 5);
    label(mark.name, mark.x, mark.y, on ? '#e0b15a' : '#ebe4d4');
  }
  for (const town of TOWNS) pin(town.post.x, town.post.y, '#e0b15a', 6);
  for (const fire of opts.fires) pin(fire.x, fire.y, fire.home ? '#e07a3d' : '#8a4a32', 4);
  for (const job of opts.jobs) pin(job.x, job.y, '#e0b15a', 4);
  if (opts.event) {
    pin(opts.event.x, opts.event.y, '#b48cdc', 8);
    label(opts.event.name, opts.event.x, opts.event.y, '#b48cdc');
  }
  for (const other of opts.others) {
    const me = other.id === opts.youId;
    pin(other.x, other.y, me ? '#f4f1ea' : '#7fbfb2', me ? 7 : 5);
    if (!me) label(other.name, other.x, other.y, '#7fbfb2');
  }
  if (opts.you && opts.you.y >= DRAWN_H * TILE) {
    const text = 'You are off the map. Nobody drew this part.';
    ctx.font = '14px Georgia, serif';
    const tw = ctx.measureText(text).width;
    ctx.fillStyle = 'rgba(12,10,8,0.85)';
    ctx.fillRect(w / 2 - tw / 2 - 10, oy + 12, tw + 20, 26);
    ctx.fillStyle = '#e9e2cf';
    ctx.fillText(text, w / 2 - tw / 2, oy + 25);
  }
  return { scale, ox, oy };
}
